import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import { Card, CardHeader, CardContent } from "../../../components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/tabs";
import { Calendar } from "../../../components/ui/calendar";
import {
  AppSelect,
  AppSearchSelect,
  AppInput,
  AppSearchSectionSelect,
} from "../../../components/appComponents";
import Common from "../../../helper/common";
import {
  DayOfWeekEnum,
  BookingStatusEnum,
  PaymentStatusEnum,
  ToCSharpFormat,
  TimeFormat,
  DateminFormat,
  respEnum,
  BookingMasterTypeEnum,
} from "../../../constants/appConstant";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import Swal from "sweetalert2";

const AddMultiBooking = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("recurringDays");
  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [courtList, setCourtList] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [numberOfPlayers, setNumberOfPlayers] = useState(4);
  const [courtPrice, setCourtPrice] = useState(0);
  const [paymentStatus] = useState(PaymentStatusEnum.Un_Paid);
  const [receivedAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [generatedBookings, setGeneratedBookings] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [manualBookings, setManualBookings] = useState([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [checkedBookings, setCheckedBookings] = useState([]);

  useEffect(() => {
    getCustomers();
    getCourts();
  }, []);

  const getCustomers = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "ClubPlayersddl",
        {},
        "Get",
        false
      );
      if (response?.data) {
        setCustomerList(response.data);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const getCourts = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetSportWiseCourtddl"
      );
      if (response?.status === respEnum.Success) {
        setCourtList(response?.data ?? []);
      }
    } catch (error) {
      console.error("Error fetching courts:", error);
    }
  };

  const handleDayToggle = (dayId) => {
    setSelectedDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId]
    );
  };

  const handleDateSelect = (date) => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < currentDate) {
      toast.error("Cannot select dates in the past");
      return;
    }

    const formattedDate = moment(date).format(DateminFormat);
    setSelectedDates((prev) =>
      prev.includes(formattedDate)
        ? prev.filter((d) => d !== formattedDate)
        : [...prev, formattedDate]
    );
  };

  const validateRecurringForm = () => {
    if (!fromDate) return "Please select a start date";
    if (!tillDate) return "Please select an end date";
    if (moment(tillDate).isBefore(moment(fromDate)))
      return "End date cannot be before start date";
    if (!startTime) return "Please select a start time";
    if (!endTime) return "Please select an end time";
    if (endTime <= startTime) return "End time must be after start time";
    if (selectedDays.length === 0)
      return "Please select at least one day of the week";
    if (!selectedCourt) return "Please select a court";
    if (!selectedCustomer) return "Please select a customer";
    if (!numberOfPlayers || numberOfPlayers < 1)
      return "Please enter a valid number of players";
    if (!courtPrice || courtPrice <= 0)
      return "Please enter a valid court price";
    return null;
  };

  const validateSpecificDatesForm = () => {
    if (selectedDates.length === 0) return "Please select at least one date";
    if (!startTime) return "Please select a start time";
    if (!endTime) return "Please select an end time";
    if (endTime <= startTime) return "End time must be after start time";
    if (!selectedCourt) return "Please select a court";
    if (!selectedCustomer) return "Please select a customer";
    if (!numberOfPlayers || numberOfPlayers < 1)
      return "Please enter a valid number of players";
    if (!courtPrice || courtPrice <= 0)
      return "Please enter a valid court price";
    return null;
  };

  const generateBookingsFromRecurring = () => {
    const from = moment(fromDate);
    const till = moment(tillDate);
    const bookings = [];

    for (
      let date = from.clone();
      date.isSameOrBefore(till);
      date.add(1, "days")
    ) {
      const dayOfWeek = date.day();

      if (selectedDays.includes(dayOfWeek)) {
        const bookingDate = date.format(DateminFormat);

        bookings.push({
          bookingDate,
          startTime,
          endTime,
          courtId: selectedCourt.value,
          courtName: selectedCourt.label,
          customerId: selectedCustomer,
          numberOfPlayers,
          courtPrice,
          paymentStatus,
          receivedAmount,
        });
      }
    }

    return bookings;
  };

  const generateBookingsFromSpecificDates = () => {
    return selectedDates.map((date) => ({
      bookingDate: date,
      startTime,
      endTime,
      courtId: selectedCourt.value,
      courtName: selectedCourt.label,
      customerId: selectedCustomer,
      numberOfPlayers,
      courtPrice,
      paymentStatus,
      receivedAmount,
    }));
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return null;
    try {
      return moment(`${date} ${time}`).format(ToCSharpFormat);
    } catch (error) {
      console.error("Date formatting error:", error);
      return null;
    }
  };

  const checkSlotsAvailability = async (bookings) => {
    try {
      setIsCheckingAvailability(true);

      if (!bookings || bookings.length === 0) {
        toast.error("No booking slots to check");
        setIsCheckingAvailability(false);
        return;
      }
      const slotsToCheck = bookings
        .map((booking) => ({
          startdatetime: formatDateTime(booking.bookingDate, booking.startTime),
          enddatetime: formatDateTime(booking.bookingDate, booking.endTime),
          courtid: booking.courtId,
          courtcharges: parseFloat(booking.courtPrice) || 0,
          isAvailable: true,
          reason: "",
        }))
        .filter((slot) => slot.startdatetime && slot.enddatetime);

      if (slotsToCheck.length === 0) {
        toast.error("Invalid date/time format in bookings");
        setIsCheckingAvailability(false);
        return;
      }

      const response = await Common.ApiService.getInstance().request(
        "CheckBulkAvailibilty",
        slotsToCheck,
        "Post"
      );

      if (response?.status === respEnum.Success) {
        const availabilityResults = response.data || [];

        if (availabilityResults.length !== bookings.length) {
          toast.warning("Received partial availability data from server");
        }

        const updatedBookings = bookings.map((booking, index) => {
          const result = availabilityResults[index] || {
            isAvailable: false,
            reason: "No availability data",
          };
          return {
            ...booking,
            isAvailable: result.isAvailable,
            reason: result.reason || "",
          };
        });

        setCheckedBookings(updatedBookings);

        const unavailable = updatedBookings.filter((b) => !b.isAvailable);
        setUnavailableSlots(
          unavailable.map((slot) => ({
            bookingDate: slot.bookingDate,
            startTime: slot.startTime,
            endTime: slot.endTime,
            courtName: slot.courtName,
            reason: slot.reason,
          }))
        );

        const available = updatedBookings.filter((b) => b.isAvailable);
        setGeneratedBookings(available);

        toast.success(
          `Found ${available.length} available slots and ${unavailable.length} unavailable slots.`
        );
      } else {
        toast.error(response?.message || "Error checking slot availability");
      }
    } catch (error) {
      console.error("Error checking slot availability:", error);
      toast.error("Error checking slot availability. Please try again.");
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Add a helper function to check if two time slots overlap
  const doSlotsOverlap = (slot1, slot2) => {
    // Convert booking dates and times to Date objects for comparison
    const slot1Start = new Date(`${slot1.bookingDate}T${slot1.startTime}`);
    const slot1End = new Date(`${slot1.bookingDate}T${slot1.endTime}`);
    const slot2Start = new Date(`${slot2.bookingDate}T${slot2.startTime}`);
    const slot2End = new Date(`${slot2.bookingDate}T${slot2.endTime}`);

    // Check for overlap (if one slot starts before the other ends)
    return slot1Start < slot2End && slot1End > slot2Start;
  };

  // Add a helper function to check if two slots are the same
  const areSlotsEqual = (slot1, slot2) => {
    return (
      slot1.bookingDate === slot2.bookingDate &&
      slot1.startTime === slot2.startTime &&
      slot1.endTime === slot2.endTime &&
      slot1.courtId === slot2.courtId
    );
  };

  // Add this function after the areSlotsEqual function
  const validateTimeFormat = () => {
    const errors = [];

    // Check start time
    if (startTime && startTime !== "23:59") {
      const minutes = startTime.split(":")[1];
      if (minutes !== "00" && minutes !== "30") {
        errors.push(
          `Invalid time format for Start Time. Should be in 00, 30, or 23:59 format.`
        );
      }
    }

    // Check end time
    if (endTime && endTime !== "23:59") {
      const minutes = endTime.split(":")[1];
      if (minutes !== "00" && minutes !== "30") {
        errors.push(
          `Invalid time format for End Time. Should be in 00, 30, or 23:59 format.`
        );
      }
    }

    return errors.length > 0 ? errors : null;
  };

  // Update the handlePreviewBookings function to include time validation
  const handlePreviewBookings = () => {
    let validationError = null;
    let bookings = [];

    // Validate time format first
    const timeFormatErrors = validateTimeFormat();
    if (timeFormatErrors) {
      toast.error(timeFormatErrors[0]);
      return;
    }

    if (activeTab === "recurringDays") {
      validationError = validateRecurringForm();
      if (!validationError) {
        bookings = generateBookingsFromRecurring();

        // Check for overlapping bookings within the generated list
        const invalidIndices = new Set();

        for (let i = 0; i < bookings.length; i++) {
          if (invalidIndices.has(i)) continue;

          for (let j = i + 1; j < bookings.length; j++) {
            if (invalidIndices.has(j)) continue;

            if (
              bookings[i].courtId === bookings[j].courtId &&
              doSlotsOverlap(bookings[i], bookings[j])
            ) {
              // Mark both bookings for removal
              invalidIndices.add(i);
              invalidIndices.add(j);

              // Add warning about overlapping recurring bookings
              toast.warning(
                `Some bookings overlap on the same court and were removed.`
              );
              break;
            }
          }
        }

        // Filter out overlapping bookings
        bookings = bookings.filter((_, index) => !invalidIndices.has(index));
      }
    } else {
      // For specific dates, we handle this in handleAddBooking
      validationError = validateSpecificDatesForm();
      if (!validationError) {
        bookings = generateBookingsFromSpecificDates();
      }
    }

    // Rest of the function remains the same
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (bookings.length === 0) {
      toast.error(
        "No bookings match your criteria. Please adjust your selections."
      );
      return;
    }

    // For recurring days, check availability before showing preview
    if (activeTab === "recurringDays") {
      checkSlotsAvailability(bookings);
      setIsPreviewMode(true);
    } else {
      // For specific dates, we'll check availability when clicking "Add Booking"
      setGeneratedBookings(bookings);
      setIsPreviewMode(true);
    }
  };

  const handleSubmitBulkBookings = async (bookingsToCreate) => {
    try {
      setIsLoading(true);

      if (!bookingsToCreate || bookingsToCreate.length === 0) {
        toast.error("No bookings to create");
        setIsLoading(false);
        return;
      }

      if (!selectedCustomer) {
        toast.error("Please select a customer");
        setIsLoading(false);
        return;
      }

      const bulkBookingData = {
        type:
          activeTab === "recurringDays"
            ? BookingMasterTypeEnum.Recurring_Days
            : BookingMasterTypeEnum.Specific_Dates,

        fromdatetime:
          activeTab === "recurringDays" && fromDate
            ? moment(fromDate).format(ToCSharpFormat)
            : null,
        enddatetime:
          activeTab === "recurringDays" && tillDate
            ? moment(tillDate).format(ToCSharpFormat)
            : null,

        playerId: selectedCustomer,
        noofplayers: parseInt(numberOfPlayers) || 1,

        days:
          activeTab === "recurringDays"
            ? selectedDays.map((day) => ({ day }))
            : [],

        bookings: bookingsToCreate
          .map((booking) => ({
            startdatetime: formatDateTime(
              booking.bookingDate,
              booking.startTime
            ),
            enddatetime: formatDateTime(booking.bookingDate, booking.endTime),
            courtid: booking.courtId,
            courtcharges: parseFloat(booking.courtPrice) || 0,
            isAvailable: true,
            reason: "",
          }))
          .filter((b) => b.startdatetime && b.enddatetime),

        total: bookingsToCreate.reduce(
          (sum, booking) => sum + (parseFloat(booking.courtPrice) || 0),
          0
        ),
      };

      if (bulkBookingData.bookings.length === 0) {
        toast.error("No valid bookings to create");
        setIsLoading(false);
        return;
      }

      const response = await Common.ApiService.getInstance().request(
        "AddBulkBooking",
        bulkBookingData,
        "Post"
      );

      if (response?.status === respEnum.Success) {
        Swal.fire({
          title: "Success!",
          html: `Successfully created <b>${bookingsToCreate.length}</b> bookings.<br><br>Redirecting to booking list...`,
          icon: "success",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        }).then(() => {
          navigate("/app/clubbookings");
        });
      } else {
        Swal.fire({
          title: "Error",
          text: response?.message || "Error creating bookings",
          icon: "error",
          confirmButtonColor: "#0C1B3A",
        });
      }
    } catch (error) {
      console.error("Error submitting bookings:", error);
      Swal.fire({
        title: "Error",
        text: "Error submitting bookings. Please try again.",
        icon: "error",
        confirmButtonColor: "#0C1B3A",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveGeneratedBooking = (index) => {
    setGeneratedBookings((prev) => prev.filter((_, i) => i !== index));
    toast.success("Booking removed successfully");
  };

  const renderBookingPreview = () => {
    return (
      <div className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-[#0C1B3A] flex items-center">
            <Icon icon="heroicons:clipboard-document-check" className="mr-2" />
            Booking Preview
          </h2>
          <Button
            variant="outline"
            onClick={() => setIsPreviewMode(false)}
            className="border-[#0C1B3A] text-[#0C1B3A] hover:bg-[#e8eaf0]"
          >
            <Icon icon="heroicons:pencil-square" className="mr-1" />
            Edit Bookings
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Available Slots */}
          <Card className="shadow-sm border-0 overflow-hidden">
            <CardHeader className="py-2 px-4 bg-[#0C1B3A] text-white flex justify-between items-center">
              <div className="flex items-center">
                <Icon
                  icon="heroicons:check-circle"
                  className="text-green-400 mr-2"
                />
                <h3 className="text-sm font-medium">
                  Available Slots ({generatedBookings.length})
                </h3>
              </div>
              {generatedBookings.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setGeneratedBookings([])}
                  className="h-8 px-2 text-white hover:text-red-200 hover:bg-[#1a2a4e]"
                >
                  <Icon icon="heroicons:trash" className="mr-1" />
                  Clear All
                </Button>
              )}
            </CardHeader>
            <div className="p-0">
              <div className="overflow-x-auto">
                {generatedBookings.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#e8eaf0]">
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Date
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Time
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Court
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Price
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {generatedBookings.map((booking, index) => (
                        <tr key={index} className="hover:bg-[#f8f9fc]">
                          <td className="py-2 px-3 text-sm">
                            {moment(booking.bookingDate).format("DD MMM YYYY")}
                            <span className="text-xs text-gray-500 ml-1">
                              ({moment(booking.bookingDate).format("ddd")})
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm whitespace-nowrap">{`${booking.startTime} - ${booking.endTime}`}</td>
                          <td className="py-2 px-3 text-sm">
                            {booking.courtName}
                          </td>
                          <td className="py-2 px-3 text-sm">
                            {booking.courtPrice}
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                handleRemoveGeneratedBooking(index)
                              }
                              className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Icon icon="heroicons:trash" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-6 text-center text-gray-500">
                    No available slots match your criteria.
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Unavailable Slots */}
          <Card className="shadow-sm border-0 overflow-hidden">
            <CardHeader className="py-2 px-4 bg-[#0C1B3A] text-white">
              <div className="flex items-center">
                <Icon icon="heroicons:x-circle" className="text-red-400 mr-2" />
                <h3 className="text-sm font-medium">
                  Unavailable Slots ({unavailableSlots.length})
                </h3>
              </div>
            </CardHeader>
            <div className="p-0">
              <div className="overflow-x-auto">
                {unavailableSlots.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#e8eaf0]">
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Date
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Time
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Court
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {unavailableSlots.map((slot, index) => (
                        <tr key={index} className="hover:bg-[#f8f9fc]">
                          <td className="py-2 px-3 text-sm">
                            {moment(slot.bookingDate).format("DD MMM YYYY")}
                            <span className="text-xs text-gray-500 ml-1">
                              ({moment(slot.bookingDate).format("ddd")})
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm whitespace-nowrap">{`${slot.startTime} - ${slot.endTime}`}</td>
                          <td className="py-2 px-3 text-sm">
                            {slot.courtName}
                          </td>
                          <td className="py-2 px-3 text-sm text-red-500">
                            {slot.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-6 text-center text-gray-500">
                    No conflicts found with your selected time slots.
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-4 flex gap-4 justify-end">
          <Button
            variant="default"
            onClick={() => {
              const availableBookings = generatedBookings.filter(
                (b) => b.isAvailable !== false
              );

              Swal.fire({
                title: "Confirm Bookings",
                html: `Are you sure you want to create all <b>${availableBookings.length}</b> bookings?<br><br>This action cannot be reverted or undone.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#0C1B3A",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, Create Bookings",
                cancelButtonText: "Cancel",
              }).then((result) => {
                if (result.isConfirmed) {
                  handleSubmitBulkBookings(availableBookings);
                }
              });
            }}
            disabled={
              isLoading ||
              generatedBookings.filter((b) => b.isAvailable !== false)
                .length === 0
            }
            className="bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white border-0"
          >
            {isLoading ? (
              <>
                <Icon icon="eos-icons:loading" className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Icon icon="heroicons:check" className="mr-1" />
                Create All Bookings
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderBookingForm = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-1">
        <AppSearchSectionSelect
          value={selectedCourt}
          onChange={setSelectedCourt}
          placeholder="Select Court"
          label="Court *"
          data={courtList}
          className="mb-3"
        />

        <AppSearchSelect
          value={selectedCustomer}
          onChange={setSelectedCustomer}
          placeholder="Select Customer"
          label="Customer *"
          data={customerList}
          className="mb-3"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <AppInput
          type="number"
          value={numberOfPlayers}
          onChange={setNumberOfPlayers}
          label="Number of Players *"
          min={1}
          max={20}
          className="mb-3"
        />

        <AppInput
          type="number"
          value={courtPrice}
          onChange={setCourtPrice}
          label="Court Price *"
          min={0}
          className="mb-3"
        />
      </div>
    </>
  );

  // Update the handleAddBooking function to include time validation
  const handleAddBooking = async () => {
    // Validate time format first
    const timeFormatErrors = validateTimeFormat();
    if (timeFormatErrors) {
      toast.error(timeFormatErrors[0]);
      return;
    }

    // Validate required fields
    if (!startTime) {
      toast.error("Please select a start time");
      return;
    }

    if (!endTime) {
      toast.error("Please select an end time");
      return;
    }

    if (endTime <= startTime) {
      toast.error("End time must be after start time");
      return;
    }

    if (!courtPrice || courtPrice <= 0) {
      toast.error("Please enter a valid court price");
      return;
    }

    if (!selectedCourt) {
      toast.error("Please select a court");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }

    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    // Create booking objects for each selected date
    const newBookings = selectedDates.map((date) => ({
      id: Date.now() + Math.random(), // Generate a unique ID for each booking
      bookingDate: date,
      startTime,
      endTime,
      courtId: selectedCourt.value,
      courtName: selectedCourt.label,
      customerId: selectedCustomer,
      customerName:
        customerList.find((c) => c.id === selectedCustomer)?.name || "Unknown",
      numberOfPlayers,
      courtPrice,
      paymentStatus,
      receivedAmount: 0,
    }));

    // Check for duplicates and overlaps with existing bookings
    const invalidBookings = [];
    const validBookings = [];

    newBookings.forEach((newBooking) => {
      // Check against existing manualBookings for the same court
      const sameCourtBookings = manualBookings.filter(
        (b) => b.courtId === newBooking.courtId
      );

      let isDuplicate = false;
      let isOverlapping = false;
      let conflictingBooking = null;

      for (const existingBooking of sameCourtBookings) {
        if (areSlotsEqual(existingBooking, newBooking)) {
          isDuplicate = true;
          conflictingBooking = existingBooking;
          break;
        }

        if (doSlotsOverlap(existingBooking, newBooking)) {
          isOverlapping = true;
          conflictingBooking = existingBooking;
          break;
        }
      }

      if (isDuplicate) {
        invalidBookings.push({
          ...newBooking,
          isAvailable: false,
          reason: "Duplicate booking - already added",
        });
      } else if (isOverlapping) {
        const conflictTime = `${conflictingBooking.startTime}-${conflictingBooking.endTime}`;
        invalidBookings.push({
          ...newBooking,
          isAvailable: false,
          reason: `Overlaps with existing booking (${conflictTime})`,
        });
      } else {
        validBookings.push(newBooking);
      }
    });

    // If there are any invalid bookings, add them to the list as unavailable
    if (invalidBookings.length > 0) {
      setManualBookings((prev) => [...prev, ...invalidBookings]);

      // Show notification about invalid bookings
      if (invalidBookings.length === newBookings.length) {
        toast.error(
          `All ${invalidBookings.length} booking(s) have conflicts and cannot be added.`
        );
        setSelectedDates([]);
        return;
      } else {
        toast.warning(
          `${invalidBookings.length} of ${newBookings.length} booking(s) have conflicts and will be marked as unavailable.`
        );
      }
    }

    // Continue with server-side validation for valid bookings only
    if (validBookings.length > 0) {
      try {
        setIsCheckingAvailability(true);

        // Convert bookings to match the API data structure
        const slotsToCheck = validBookings.map((booking) => ({
          startdatetime: moment(
            `${booking.bookingDate}T${booking.startTime}`
          ).format(ToCSharpFormat),
          enddatetime: moment(
            `${booking.bookingDate}T${booking.endTime}`
          ).format(ToCSharpFormat),
          courtid: booking.courtId,
          courtcharges: booking.courtPrice,
          isAvailable: true,
          reason: "",
        }));

        // Call the API
        const response = await Common.ApiService.getInstance().request(
          "CheckBulkAvailibilty",
          slotsToCheck,
          "Post"
        );

        if (response?.status === respEnum.Success) {
          // Update the bookings with availability information
          const availabilityResults = response.data;
          const updatedBookings = validBookings.map((booking, index) => {
            const result = availabilityResults[index];
            return {
              ...booking,
              isAvailable: result.isAvailable,
              reason: result.reason || "",
            };
          });

          // Add all bookings (both available and unavailable) to the list
          setManualBookings((prev) => [...prev, ...updatedBookings]);

          // Count available and unavailable slots
          const available = updatedBookings.filter((b) => b.isAvailable);
          const unavailable = updatedBookings.filter((b) => !b.isAvailable);

          // Show success message
          if (invalidBookings.length > 0) {
            toast.success(
              `Added ${available.length} available and ${
                unavailable.length + invalidBookings.length
              } unavailable bookings.`
            );
          } else {
            toast.success(
              `Added ${available.length} available and ${unavailable.length} unavailable bookings.`
            );
          }
        } else {
          toast.error(response?.message || "Error checking slot availability");
        }
      } catch (error) {
        console.error("Error checking slot availability:", error);
        toast.error("Error checking slot availability. Please try again.");
      } finally {
        setIsCheckingAvailability(false);
      }
    }

    // Reset the selection fields
    setSelectedDates([]);
  };

  const handleRemoveBooking = (bookingId) => {
    setManualBookings((prev) =>
      prev.filter((booking) => booking.id !== bookingId)
    );
    toast.success("Booking removed successfully");
  };

  const handleProceedToPreview = () => {
    const availableBookings = manualBookings.filter(
      (b) => b.isAvailable !== false
    );

    if (availableBookings.length === 0) {
      toast.error("Please add at least one available booking");
      return;
    }

    Swal.fire({
      title: "Confirm Bookings",
      html: `Are you sure you want to create all <b>${availableBookings.length}</b> bookings?<br><br>This action cannot be reverted or undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0C1B3A",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Create Bookings",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmitBulkBookings(availableBookings);
      }
    });
  };

  const renderSpecificDatesContent = () => (
    <TabsContent value="specificDates">
      <div className="pt-4">
        <Card className="mb-4 border-0 shadow-sm overflow-hidden">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              {/* Left column - Calendar */}
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                  Select Date(s) *
                </label>
                <div className="border rounded-md p-1">
                  {renderCalendarSelector()}
                </div>
                <div className="text-xs text-gray-500 mt-1 flex items-center">
                  <Icon
                    icon="heroicons:calendar"
                    className="mr-1 text-[#0C1B3A]"
                  />
                  Selected: {selectedDates.length} date(s)
                </div>
              </div>

              <div className="col-span-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <AppInput
                    type="time"
                    value={startTime}
                    onChange={setStartTime}
                    label="Start Time *"
                    className="text-sm"
                  />

                  <AppInput
                    type="time"
                    value={endTime}
                    onChange={setEndTime}
                    label="End Time *"
                    className="text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AppSearchSectionSelect
                    value={selectedCourt}
                    onChange={setSelectedCourt}
                    placeholder="Select Court"
                    label="Court *"
                    data={courtList}
                    className="text-sm"
                  />

                  <AppSearchSelect
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    placeholder="Select Customer"
                    label="Customer *"
                    data={customerList}
                    className="text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AppInput
                    type="number"
                    value={numberOfPlayers}
                    onChange={setNumberOfPlayers}
                    label="Players *"
                    min={1}
                    max={20}
                    className="text-sm"
                  />

                  <AppInput
                    type="number"
                    value={courtPrice}
                    onChange={setCourtPrice}
                    label="Price *"
                    min={0}
                    className="text-sm"
                  />
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleAddBooking}
                    disabled={isCheckingAvailability}
                    className="flex items-center bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white border-0"
                  >
                    {isCheckingAvailability ? (
                      <>
                        <Icon
                          icon="eos-icons:loading"
                          className="mr-2 animate-spin"
                        />
                        Checking Slot Availability...
                      </>
                    ) : (
                      <>
                        <Icon icon="heroicons:plus-circle" className="mr-1" />
                        Add Booking
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Available Slots Card */}
          <Card className="shadow-sm border-0 overflow-hidden">
            <CardHeader className="py-3 px-4 flex justify-between items-center bg-[#0C1B3A] text-white">
              <div className="flex items-center">
                <Icon
                  icon="heroicons:check-circle"
                  className="text-green-400 mr-2"
                />
                <h3 className="text-sm font-medium">
                  Available Slots (
                  {manualBookings.filter((b) => b.isAvailable !== false).length}
                  )
                </h3>
              </div>
              {manualBookings.filter((b) => b.isAvailable !== false).length >
                0 && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setManualBookings((prev) =>
                      prev.filter((b) => b.isAvailable === false)
                    )
                  }
                  className="h-8 px-2 text-white hover:text-red-200 hover:bg-[#1a2a4e]"
                >
                  <Icon icon="heroicons:trash" className="mr-1" />
                  Clear All
                </Button>
              )}
            </CardHeader>
            <div className="p-0">
              <div className="overflow-x-auto">
                {manualBookings.filter((b) => b.isAvailable !== false).length >
                0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#e8eaf0]">
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Date
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Time
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Court
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Customer
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Price
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {manualBookings
                        .filter((b) => b.isAvailable !== false)
                        .map((booking) => (
                          <tr key={booking.id} className="hover:bg-[#f8f9fc]">
                            <td className="py-2 px-3 text-sm">
                              {moment(booking.bookingDate).format(
                                "DD MMM YYYY"
                              )}
                              <span className="text-xs text-gray-500 ml-1">
                                ({moment(booking.bookingDate).format("ddd")})
                              </span>
                            </td>
                            <td className="py-2 px-3 text-sm whitespace-nowrap">
                              {`${booking.startTime} - ${booking.endTime}`}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              {booking.courtName}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              {booking.customerName}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              {booking.courtPrice}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              <Button
                                variant="ghost"
                                onClick={() => handleRemoveBooking(booking.id)}
                                className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Icon icon="heroicons:trash" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-6 text-center text-gray-500">
                    No available bookings added yet.
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Unavailable Slots Card */}
          <Card className="shadow-sm border-0 overflow-hidden">
            <CardHeader className="py-3 px-4 flex justify-between items-center bg-[#0C1B3A] text-white">
              <div className="flex items-center">
                <Icon icon="heroicons:x-circle" className="text-red-400 mr-2" />
                <h3 className="text-sm font-medium">
                  Unavailable Slots (
                  {manualBookings.filter((b) => b.isAvailable === false).length}
                  )
                </h3>
              </div>
              {manualBookings.filter((b) => b.isAvailable === false).length >
                0 && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setManualBookings((prev) =>
                      prev.filter((b) => b.isAvailable !== false)
                    )
                  }
                  className="h-8 px-2 text-white hover:text-red-200 hover:bg-[#1a2a4e]"
                >
                  <Icon icon="heroicons:trash" className="mr-1" />
                  Clear All
                </Button>
              )}
            </CardHeader>
            <div className="p-0">
              <div className="overflow-x-auto">
                {manualBookings.filter((b) => b.isAvailable === false).length >
                0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#e8eaf0]">
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Date
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Time
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Court
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-[#0C1B3A]">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {manualBookings
                        .filter((b) => b.isAvailable === false)
                        .map((booking) => (
                          <tr key={booking.id} className="hover:bg-[#f8f9fc]">
                            <td className="py-2 px-3 text-sm">
                              {moment(booking.bookingDate).format(
                                "DD MMM YYYY"
                              )}
                              <span className="text-xs text-gray-500 ml-1">
                                ({moment(booking.bookingDate).format("ddd")})
                              </span>
                            </td>
                            <td className="py-2 px-3 text-sm whitespace-nowrap">
                              {`${booking.startTime} - ${booking.endTime}`}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              {booking.courtName}
                            </td>
                            <td className="py-2 px-3 text-sm text-red-500">
                              {booking.reason || "Not available"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-6 text-center text-gray-500">
                    No unavailable slots detected.
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex gap-4 justify-end">
          <Button
            onClick={handleProceedToPreview}
            disabled={
              manualBookings.filter((b) => b.isAvailable !== false).length === 0
            }
            className="bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white border-0"
          >
            <Icon icon="heroicons:check" className="mr-1" />
            Create All Bookings
          </Button>
        </div>
      </div>
    </TabsContent>
  );

  const renderRecurringDaysContent = () => (
    <TabsContent value="recurringDays">
      <div className="pt-4">
        <Card className="mb-4 border-0 shadow-sm overflow-visible">
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-4">
              {/* Date & Time Selection */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                    From Date *
                  </label>
                  <AppInput
                    type="date"
                    value={fromDate}
                    onChange={setFromDate}
                    min={today}
                    className="mb-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                    Till Date *
                  </label>
                  <AppInput
                    type="date"
                    value={tillDate}
                    onChange={setTillDate}
                    min={fromDate || today}
                    className="mb-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                    Start Time *
                  </label>
                  <AppInput
                    type="time"
                    value={startTime}
                    onChange={setStartTime}
                    className="mb-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                    End Time *
                  </label>
                  <AppInput
                    type="time"
                    value={endTime}
                    onChange={setEndTime}
                    className="mb-0"
                  />
                </div>
              </div>

              {/* Day Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-[#0C1B3A]">
                  Select Days *
                </label>
                {renderDaySelector()}
              </div>

              {/* Fix Court & Customer selection layout */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
                <div className="md:col-span-4">
                  <AppSearchSectionSelect
                    value={selectedCourt}
                    onChange={setSelectedCourt}
                    placeholder="Select Court"
                    label="Court *"
                    data={courtList}
                    className="mb-0 z-30"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>

                <div className="md:col-span-4">
                  <AppSearchSelect
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    placeholder="Select Customer"
                    label="Customer *"
                    data={customerList}
                    className="mb-0 z-20"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9998,
                      }),
                    }}
                  />
                </div>

                <div className="md:col-span-1">
                  <AppInput
                    type="number"
                    value={numberOfPlayers}
                    onChange={setNumberOfPlayers}
                    label="Players *"
                    min={1}
                    max={20}
                    className="mb-0"
                  />
                </div>

                <div className="md:col-span-3">
                  <AppInput
                    type="number"
                    value={courtPrice}
                    onChange={setCourtPrice}
                    label="Court Price *"
                    min={0}
                    className="mb-0"
                  />
                </div>
              </div>

              {/* Preview button */}
              <div className="flex justify-end">
                <Button
                  variant="default"
                  onClick={handlePreviewBookings}
                  disabled={isCheckingAvailability}
                  className="bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white border-0"
                >
                  {isCheckingAvailability ? (
                    <>
                      <Icon
                        icon="eos-icons:loading"
                        className="mr-2 animate-spin"
                      />
                      Checking Availability...
                    </>
                  ) : (
                    <>
                      <Icon icon="heroicons:eye" className="mr-1" />
                      Preview Bookings
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {isPreviewMode && renderBookingPreview()}
      </div>
    </TabsContent>
  );

  const renderCalendarSelector = () => {
    return (
      <div className="my-4">
        <style>
          {`
            

            .rdp-button:hover:not([disabled]) {
              color: grey !important;
            }

          `}
        </style>

        <Calendar
          mode="multiple"
          selected={selectedDates.map((date) => new Date(date))}
          onSelect={(dates) =>
            setSelectedDates(
              dates.map((date) => moment(date).format(DateminFormat))
            )
          }
          disabled={{ before: new Date() }}
          className="rounded-md border"
        />
      </div>
    );
  };

  const renderDaySelector = () => {
    return (
      <div className="flex flex-wrap gap-2">
        {Common.Utility.enumToArray(DayOfWeekEnum).map((day) => (
          <Button
            key={day.id}
            variant={selectedDays.includes(day.id) ? "default" : "outline"}
            onClick={() => handleDayToggle(day.id)}
            className="w-24"
          >
            {day.name}
          </Button>
        ))}
      </div>
    );
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="container mx-auto p-3">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold text-[#0C1B3A] flex items-center">
          <Icon icon="heroicons:calendar" className="mr-2" />
          Create Multiple Bookings
        </h1>
        <Button
          variant="outline"
          onClick={() => navigate("/app/clubbookings")}
          className="border-[#0C1B3A] text-[#0C1B3A] hover:bg-primary"
        >
          <Icon icon="heroicons:arrow-left" className="mr-1" /> Back
        </Button>
      </div>

      {isPreviewMode ? (
        renderBookingPreview()
      ) : (
        <Card className="p-4 border-0 shadow-sm">
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#e8eaf0]">
              <TabsTrigger
                value="recurringDays"
                className="data-[state=active]:bg-[#0C1B3A] data-[state=active]:text-white"
              >
                Recurring Days
              </TabsTrigger>
              <TabsTrigger
                value="specificDates"
                className="data-[state=active]:bg-[#0C1B3A] data-[state=active]:text-white"
              >
                Specific Dates
              </TabsTrigger>
            </TabsList>

            {renderRecurringDaysContent()}
            {renderSpecificDatesContent()}
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default AddMultiBooking;
